export type SimulationLanguage = 'sr' | 'en';

export type SimAssignDevice = {
  id: string;
  name: string;
  location: string;
};

export type SimAssignEmployee = {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  cardNumber: string;
};

type Pool = {
  devices: SimAssignDevice[];
  employees: SimAssignEmployee[];
  initialAssignedIds: string[];
};

const POOLS: Record<SimulationLanguage, Pool> = {
  sr: {
    devices: [
      { id: 'dev-main', name: 'Glavni ulaz', location: 'Novi Sad' },
      { id: 'dev-office', name: 'Kancelarija II sprat', location: 'Beograd' },
      { id: 'dev-wh', name: 'Magacin, rampa', location: 'Niš' },
    ],
    employees: [
      {
        id: 'emp-1',
        firstName: 'Marko',
        lastName: 'Jovanović',
        cardNumber: '100128',
        name: 'Marko Jovanović',
      },
      { id: 'emp-2', firstName: 'Ana', lastName: 'Nikolić', cardNumber: '100129', name: 'Ana Nikolić' },
      {
        id: 'emp-3',
        firstName: 'Ksenija',
        lastName: 'Pavlović',
        cardNumber: '100130',
        name: 'Ksenija Pavlović',
      },
      {
        id: 'emp-4',
        firstName: 'Stefan',
        lastName: 'Đorđević',
        cardNumber: '100131',
        name: 'Stefan Đorđević',
      },
      {
        id: 'emp-5',
        firstName: 'Jelena',
        lastName: 'Popović',
        cardNumber: '100132',
        name: 'Jelena Popović',
      },
      { id: 'emp-6', firstName: 'Nikola', lastName: 'Ilić', cardNumber: '100133', name: 'Nikola Ilić' },
    ],
    initialAssignedIds: ['emp-1', 'emp-2'],
  },
  en: {
    devices: [
      { id: 'dev-main', name: 'Main entrance', location: 'Novi Sad' },
      { id: 'dev-office', name: 'Office 2nd floor', location: 'Belgrade' },
      { id: 'dev-wh', name: 'Warehouse, dock', location: 'Nis' },
    ],
    employees: [
      {
        id: 'emp-1',
        firstName: 'James',
        lastName: 'Mitchell',
        cardNumber: '200128',
        name: 'James Mitchell',
      },
      {
        id: 'emp-2',
        firstName: 'Sarah',
        lastName: 'Collins',
        cardNumber: '200129',
        name: 'Sarah Collins',
      },
      {
        id: 'emp-3',
        firstName: 'Emily',
        lastName: 'Richardson',
        cardNumber: '200130',
        name: 'Emily Richardson',
      },
      {
        id: 'emp-4',
        firstName: 'David',
        lastName: 'Brooks',
        cardNumber: '200131',
        name: 'David Brooks',
      },
      {
        id: 'emp-5',
        firstName: 'Rachel',
        lastName: 'Foster',
        cardNumber: '200132',
        name: 'Rachel Foster',
      },
      { id: 'emp-6', firstName: 'Michael', lastName: 'Chen', cardNumber: '200133', name: 'Michael Chen' },
    ],
    initialAssignedIds: ['emp-1', 'emp-2'],
  },
};

export function getAssignmentDevices(lang: SimulationLanguage): SimAssignDevice[] {
  return POOLS[lang].devices.map((d) => ({ ...d }));
}

export function getAssignmentEmployees(lang: SimulationLanguage): SimAssignEmployee[] {
  return POOLS[lang].employees.map((e) => ({ ...e }));
}

export function createAssignmentState(
  lang: SimulationLanguage,
  deviceId = 'dev-main',
  employeesOverride?: SimAssignEmployee[]
) {
  const pool = POOLS[lang];
  const assigned = new Set(
    deviceId === 'dev-main' ? pool.initialAssignedIds : deviceId === 'dev-office' ? ['emp-3'] : []
  );
  return {
    devices: getAssignmentDevices(lang),
    employees: employeesOverride ? employeesOverride.map((e) => ({ ...e })) : getAssignmentEmployees(lang),
    deviceId,
    assignedIds: assigned,
  };
}

export type AssignmentState = ReturnType<typeof createAssignmentState>;

export function splitEmployees(state: AssignmentState) {
  const available = state.employees.filter((e) => !state.assignedIds.has(e.id));
  const assigned = state.employees.filter((e) => state.assignedIds.has(e.id));
  return { available, assigned };
}
